import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Image,
  useDisclosure,
  Spinner,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Icon,
  IconButton,
} from '@chakra-ui/react';
import {
  Table,
  TableHead,
  TableHeaderSortCell,
  TableSortDirection,
  TableBody,
  TableRow,
  TableCell,
  HeadingText,
  Button,
  showToast,
} from '@spotoninc/nexus-react';
import {
  AddIcon,
  MoreVerticalIcon,
  EditIcon,
  DeleteIcon,
} from '@spotoninc/nexus-web-assets';
import PrivilegeImage from '../../media/images/PrivilegeImage.svg';
import apiEndpoint from '../config/data';
import ConfirmationDialog from '../manageRoles/ConfirmationDialog';
import ExclusionWizardModal from './exclusionWizardModal';

// Helper function for authenticated API calls
const fetchWithAuth = async (url, method = 'GET', body = null) => {
  const options = {
    method,
    credentials: 'include',
    headers: localStorage['access']
      ? { Authorization: 'Bearer ' + localStorage['access'] }
      : {},
  };

  if (body) options.body = body;

  const response = await fetch(url, options);

  if (!response.ok) {
    throw new Error(`Server responded with status: ${response.status}`);
  }

  const data = await response.json();

  if (data.code !== undefined) {
    window.open('/login', '_self');
    showToast({
      priority: 'error',
      content: 'Session expired!',
      autoClose: 5000,
    });
    throw new Error('Session expired');
  }

  return data;
};

const ManageExclusions = () => {
  const [exclusions, setExclusions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingExclusionId, setEditingExclusionId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: TableSortDirection.ascending,
  });
  const [selectedExclusion, setSelectedExclusion] = useState(null);
  const {
    isOpen: isExclusionModalOpen,
    onOpen: openExclusionModal,
    onClose: closeExclusionModal,
  } = useDisclosure();
  const [exclusionName, setExclusionName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [exclusionToDelete, setExclusionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingExclusionId, setDeletingExclusionId] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [accounts, setAccounts] = useState({});
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState({});

  // Track original values for change detection
  const [originalExclusionName, setOriginalExclusionName] = useState('');
  const [originalSelectedIntegrations, setOriginalSelectedIntegrations] =
    useState([]);
  const [originalSelectedAccounts, setOriginalSelectedAccounts] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch exclusions data
  const fetchExclusions = async (showLoadingSpinner = true) => {
    try {
      // Only show loading spinner if explicitly requested
      if (showLoadingSpinner) {
        setLoading(true);
      }

      const data = await fetchWithAuth(
        `${apiEndpoint}/api/get_exclusion_data/null/`
      );

      // Create a map of account IDs to their descriptions
      const accountMap = {};

      // Process all integration-specific account data
      if (data) {
        // Look for integration IDs (like "123") that contain account arrays
        Object.keys(data).forEach(key => {
          // Skip known objects that aren't integration data
          if (['excl_data', 'integrations', 'plparent'].includes(key)) {
            return;
          }

          // Check if this is an array of account data
          if (Array.isArray(data[key])) {
            // Store accounts data for each integration
            setAccounts(prev => ({
              ...prev,
              [key]: data[key],
            }));

            // Process all accounts in this integration
            data[key].forEach(account => {
              if (account.pk && account.desc) {
                accountMap[account.pk] = account.desc;
              }
            });

            // Also process the non-tree version if available
            if (data[`${key}_nt`] && Array.isArray(data[`${key}_nt`])) {
              data[`${key}_nt`].forEach(account => {
                if (account.pk && account.desc) {
                  accountMap[account.pk] = account.desc;
                }
              });
            }
          }
        });
      }

      // Process the exclusions data
      const processedExclusions = [];

      if (data && data.excl_data) {
        // Extract all exclusions
        Object.entries(data.excl_data).forEach(([exclusionId, details]) => {
          const assignedItems = details.assigned_plparent || [];

          // Map account IDs to their descriptions
          const accountDescriptions = assignedItems.map(
            id => accountMap[id] || `Account ${id}`
          );

          // Create the base exclusion object
          const exclusionObj = {
            id: details.id,
            name:
              details.description || details.name || `Exclusion ${exclusionId}`,
            created_date: details.created_date || null,
            assigned_integrations: details.assigned_integrations || [],
            assigned_items: assignedItems,
            account_descriptions: accountDescriptions,
          };

          // Add integration-specific account mappings
          if (
            details.assigned_integrations &&
            details.assigned_integrations.length > 0
          ) {
            details.assigned_integrations.forEach(integId => {
              const integrationKey = `assigned_plparent_${integId}`;
              if (details[integrationKey]) {
                exclusionObj[integrationKey] = details[integrationKey];
              }
            });
          }

          processedExclusions.push(exclusionObj);
        });
      }

      setExclusions(processedExclusions);

      // Extract integration data if available
      if (data && data.integrations) {
        setIntegrations(data.integrations);
      }
    } catch (error) {
      console.error('Error fetching exclusions:', error);
      showToast({
        priority: 'error',
        content: 'Failed to load exclusions',
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (key, newDirection) => {
    setSortConfig({ key, direction: newDirection });
  };

  // Format integrations display by looking up app_name
  const formatIntegrations = integrationIds => {
    if (!integrationIds?.length) return '--';

    // Map integration IDs to app names
    const integrationNames = integrationIds.map(id => {
      const integration = integrations.find(i => i.id === id);
      return integration ? integration.app_name : id;
    });

    return integrationNames.length === 1
      ? integrationNames[0]
      : `${integrationNames[0]} + ${integrationNames.length - 1} ${integrationNames.length === 2 ? 'Other' : 'Others'}`;
  };

  // Format accounts display using pre-processed account descriptions
  const formatAccounts = (accountIds, accountDescriptions) => {
    if (!accountIds?.length) return '--';

    // Make sure we have an array of descriptions that's the same length as accountIds
    const descriptions =
      Array.isArray(accountDescriptions) &&
      accountDescriptions.length === accountIds.length
        ? accountDescriptions
        : accountIds.map(id => `Account ${id}`);

    return descriptions.length === 1
      ? descriptions[0]
      : `${descriptions[0]} + ${descriptions.length - 1} ${descriptions.length === 2 ? 'Other' : 'Others'}`;
  };

  // Sort data
  const sortedData = React.useMemo(() => {
    let sortableData = [...exclusions];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        // For assigned_integrations, sort by the formatted integration names
        if (sortConfig.key === 'assigned_integrations') {
          // Use formatIntegrations to get the display names
          const aValue = formatIntegrations(a[sortConfig.key]).toLowerCase();
          const bValue = formatIntegrations(b[sortConfig.key]).toLowerCase();

          if (aValue < bValue) {
            return sortConfig.direction === TableSortDirection.ascending
              ? -1
              : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === TableSortDirection.ascending
              ? 1
              : -1;
          }
          return 0;
        }

        // For assigned_items, sort by the formatted account names
        if (sortConfig.key === 'assigned_items') {
          // Use formatAccounts to get the display names
          const aValue = formatAccounts(
            a[sortConfig.key],
            a.account_descriptions
          ).toLowerCase();
          const bValue = formatAccounts(
            b[sortConfig.key],
            b.account_descriptions
          ).toLowerCase();

          if (aValue < bValue) {
            return sortConfig.direction === TableSortDirection.ascending
              ? -1
              : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === TableSortDirection.ascending
              ? 1
              : -1;
          }
          return 0;
        }

        // For name field, compare case-insensitively
        if (sortConfig.key === 'name') {
          const aValue = a[sortConfig.key]?.toLowerCase() || '';
          const bValue = b[sortConfig.key]?.toLowerCase() || '';

          if (aValue < bValue) {
            return sortConfig.direction === TableSortDirection.ascending
              ? -1
              : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === TableSortDirection.ascending
              ? 1
              : -1;
          }
          return 0;
        }

        // For other fields, compare directly
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === TableSortDirection.ascending ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === TableSortDirection.ascending ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [
    exclusions,
    sortConfig,
    formatIntegrations,
    formatAccounts,
    integrations,
  ]);

  // Handle add exclusion
  const handleAddExclusion = () => {
    setSelectedExclusion(null);
    setExclusionName('');
    setSelectedIntegrations([]);
    setSelectedAccounts({});

    // Set original values for change detection
    setOriginalExclusionName('');
    setOriginalSelectedIntegrations([]);
    setOriginalSelectedAccounts({});
    setHasChanges(false);

    openExclusionModal();
  };

  // Handle edit exclusion
  const handleEditExclusion = exclusion => {
    try {
      // Set loading state for UI feedback
      setEditLoading(true);
      setEditingExclusionId(exclusion.id);

      // Use the exclusion data that's already passed in
      setSelectedExclusion(exclusion);
      setExclusionName(exclusion.name);
      setSelectedIntegrations(exclusion.assigned_integrations || []);

      // Set up selected accounts based on integration-specific assigned_items data
      const accountSelections = {};

      if (
        exclusion.assigned_integrations &&
        exclusion.assigned_integrations.length > 0
      ) {
        exclusion.assigned_integrations.forEach(integId => {
          // Check if there's integration-specific account data
          const integrationKey = `assigned_plparent_${integId}`;
          if (exclusion[integrationKey]) {
            // Use the integration-specific account list
            accountSelections[integId] = [...exclusion[integrationKey]];
          } else if (
            exclusion.assigned_items &&
            exclusion.assigned_items.length > 0
          ) {
            // Fallback to the general assigned_items if integration-specific data is not available
            accountSelections[integId] = [...exclusion.assigned_items];
          } else {
            // Initialize with empty array if no accounts are assigned
            accountSelections[integId] = [];
          }
        });
      }

      setSelectedAccounts(accountSelections);

      // Set original values for change detection
      setOriginalExclusionName(exclusion.name);
      setOriginalSelectedIntegrations(exclusion.assigned_integrations || []);
      setOriginalSelectedAccounts({ ...accountSelections });
      setHasChanges(false);

      openExclusionModal();
    } catch (error) {
      console.error('Error in handleEditExclusion:', error);
      showToast({
        priority: 'error',
        content: 'Failed to load exclusion details',
        autoClose: 5000,
      });
    } finally {
      setEditLoading(false);
      setEditingExclusionId(null);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = exclusionId => {
    setExclusionToDelete(exclusionId);
    setIsDeleteDialogOpen(true);
  };

  // Handle delete exclusion
  const handleDeleteExclusion = async exclusionId => {
    try {
      setIsDeleting(true);
      setDeletingExclusionId(exclusionId);

      const formData = new FormData();
      formData.append('exclId', exclusionId);
      formData.append('type', 'delete');

      await fetchWithAuth(
        `${apiEndpoint}/api/set_exclusion_data/`,
        'POST',
        formData
      );

      // Close the delete dialog
      setIsDeleteDialogOpen(false);

      // Immediately update the local state to remove the deleted exclusion
      setExclusions(prevExclusions =>
        prevExclusions.filter(exclusion => exclusion.id !== exclusionId)
      );

      // Also fetch fresh data in the background to ensure everything is in sync
      // This happens asynchronously and won't block the UI update
      // Pass false to avoid showing the loading spinner
      fetchExclusions(false).catch(error => {
        console.error('Error refreshing exclusions after delete:', error);
      });

      showToast({
        priority: 'success',
        content: 'Exclusion deleted successfully',
        autoClose: 5000,
      });
    } catch (error) {
      console.error('Error deleting exclusion:', error);
      showToast({
        priority: 'error',
        content: 'Failed to delete exclusion',
        autoClose: 5000,
      });
    } finally {
      setIsDeleting(false);
      setDeletingExclusionId(null);
    }
  };

  // We've moved these functions to the ExclusionWizardModal component

  // Helper function to create account descriptions from account IDs
  const createAccountDescriptions = accountIds => {
    // Create a map of account descriptions from the selected accounts
    const descriptions = accountIds.map(id => {
      // Look through all integrations' accounts to find the matching description
      for (const integId in accounts) {
        const accountList = accounts[integId];
        if (!accountList) continue;

        // Check both the regular accounts and the non-tree version if available
        const account = accountList.find(acc => acc.pk === id);
        if (account && account.desc) {
          return account.desc;
        }
      }
      return `Account ${id}`;
    });

    return descriptions;
  };

  // Handle submit exclusion (add or edit)
  const handleSubmitExclusion = async () => {
    try {
      if (!exclusionName.trim()) {
        showToast({
          priority: 'error',
          content: 'Exclusion name cannot be empty',
          autoClose: 5000,
        });
        return;
      }

      // Check if there are integrations selected without accounts
      const integrationsWithoutAccounts = selectedIntegrations.filter(
        integId =>
          !selectedAccounts[integId] || selectedAccounts[integId].length === 0
      );

      if (integrationsWithoutAccounts.length > 0) {
        // Get the first integration without accounts to highlight
        const integrationToHighlight = integrationsWithoutAccounts[0];

        // Trigger animation and scroll to the integration
        document.dispatchEvent(
          new CustomEvent('highlight-add-accounts', {
            detail: { integrationId: integrationToHighlight },
          })
        );

        showToast({
          priority: 'warning',
          content: 'Please add accounts for all selected integrations',
          autoClose: 5000,
        });
        return;
      }

      setIsSubmitting(true);

      const formData = new FormData();
      formData.append('exclusionName', exclusionName);
      formData.append('type', 'edit');

      if (selectedExclusion) {
        formData.append('exclId', selectedExclusion.id);
      } else {
        formData.append('exclId', '');
      }

      // When editing an existing exclusion, we need to first remove all existing integrations
      // that are not in the current selection to ensure proper cleanup
      if (selectedExclusion) {
        // Get all integrations that were previously assigned to this exclusion
        const previousIntegrations =
          selectedExclusion.assigned_integrations || [];

        // For each integration that is no longer selected, explicitly set it to false
        // This will trigger the backend to delete the mappings
        previousIntegrations.forEach(prevIntegId => {
          const stringPrevIntegId = String(prevIntegId);
          if (!selectedIntegrations.includes(stringPrevIntegId)) {
            const key = `I_${stringPrevIntegId}`;
            formData.append(key, false);
          }
        });
      }

      // Append selected integrations
      selectedIntegrations.forEach(integId => {
        const key = `I_${integId}`;
        formData.append(key, true);
      });

      // Collect all unique account IDs across all integrations
      const allAccountIds = [];

      // If editing an existing exclusion, we need to handle the case where accounts
      // have been removed from the selection
      if (selectedExclusion && selectedExclusion.assigned_items) {
        // Get all previously assigned accounts
        const previousAccounts = selectedExclusion.assigned_items || [];

        // For each integration that is still selected
        selectedIntegrations.forEach(integId => {
          const currentAccountsForInteg = selectedAccounts[integId] || [];

          // For each previously assigned account, check if it's still selected
          previousAccounts.forEach(prevAccountId => {
            // If this account is no longer selected for this integration,
            // explicitly set it to false to trigger deletion
            if (!currentAccountsForInteg.includes(prevAccountId)) {
              const key = `P_${prevAccountId}_${integId}`;
              formData.append(key, false);
            }
          });
        });
      }

      // Now add all currently selected accounts
      Object.entries(selectedAccounts).forEach(([integId, accountIds]) => {
        accountIds.forEach(accountId => {
          if (!allAccountIds.includes(accountId)) {
            allAccountIds.push(accountId);
          }

          const key = `P_${accountId}_${integId}`;
          formData.append(key, true);
        });
      });

      // Send the API request
      const response = await fetchWithAuth(
        `${apiEndpoint}/api/set_exclusion_data/`,
        'POST',
        formData
      );

      // Immediately update the local state
      if (selectedExclusion) {
        // For editing an existing exclusion
        const updatedExclusion = {
          ...selectedExclusion,
          name: exclusionName,
          assigned_integrations: selectedIntegrations,
          assigned_items: allAccountIds,
          account_descriptions: createAccountDescriptions(allAccountIds),
        };

        // Add integration-specific account mappings
        Object.entries(selectedAccounts).forEach(([integId, accountIds]) => {
          updatedExclusion[`assigned_plparent_${integId}`] = accountIds;
        });

        // Update the exclusions array with the updated exclusion
        setExclusions(prevExclusions =>
          prevExclusions.map(exclusion =>
            exclusion.id === updatedExclusion.id ? updatedExclusion : exclusion
          )
        );
      } else if (response && response.excl_data) {
        // For adding a new exclusion, try to find the newly created exclusion in the response
        // The API returns all exclusions, so we need to find the one with the matching name
        // that was just created (it should be the first one in the list)
        const newExclusionData = Object.values(response.excl_data).find(
          excl =>
            excl.description === exclusionName || excl.name === exclusionName
        );

        if (newExclusionData) {
          // Create a new exclusion object in the same format as our local state
          const newExclusion = {
            id: newExclusionData.id,
            name: exclusionName,
            created_date:
              newExclusionData.created_date || new Date().toISOString(),
            assigned_integrations: selectedIntegrations,
            assigned_items: allAccountIds,
            account_descriptions: createAccountDescriptions(allAccountIds),
          };

          // Add integration-specific account mappings
          Object.entries(selectedAccounts).forEach(([integId, accountIds]) => {
            newExclusion[`assigned_plparent_${integId}`] = accountIds;
          });

          // Add the new exclusion to the local state
          setExclusions(prevExclusions => [newExclusion, ...prevExclusions]);
        }
      }

      // Close modal
      closeExclusionModal();

      // For new exclusions or as a fallback for edits, fetch fresh data
      // Pass false to avoid showing the loading spinner
      fetchExclusions(false).catch(error => {
        console.error('Error refreshing exclusions after submit:', error);
      });

      showToast({
        priority: 'success',
        content: selectedExclusion
          ? 'Exclusion updated successfully'
          : 'Exclusion added successfully',
        autoClose: 5000,
      });
    } catch (error) {
      console.error('Error submitting exclusion:', error);
      showToast({
        priority: 'error',
        content: 'Failed to save exclusion',
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch exclusions on component mount
  useEffect(() => {
    fetchExclusions(true); // Show loading spinner on initial load
  }, []);

  // Function to check if there are any changes
  const checkForChanges = useCallback(() => {
    // Check for name changes
    const nameChanged = exclusionName !== originalExclusionName;

    // Check for integration changes
    let integrationsChanged = false;
    if (selectedIntegrations.length !== originalSelectedIntegrations.length) {
      integrationsChanged = true;
    } else {
      integrationsChanged =
        selectedIntegrations.some(
          id => !originalSelectedIntegrations.includes(id)
        ) ||
        originalSelectedIntegrations.some(
          id => !selectedIntegrations.includes(id)
        );
    }

    // Check for account changes
    let accountsChanged = false;
    const originalKeys = Object.keys(originalSelectedAccounts);
    const currentKeys = Object.keys(selectedAccounts);

    if (originalKeys.length !== currentKeys.length) {
      accountsChanged = true;
    } else {
      accountsChanged = currentKeys.some(integId => {
        const originalAccounts = originalSelectedAccounts[integId] || [];
        const currentAccounts = selectedAccounts[integId] || [];

        if (originalAccounts.length !== currentAccounts.length) {
          return true;
        }

        return (
          originalAccounts.some(id => !currentAccounts.includes(id)) ||
          currentAccounts.some(id => !originalAccounts.includes(id))
        );
      });
    }

    // Update hasChanges state
    setHasChanges(nameChanged || integrationsChanged || accountsChanged);
  }, [
    exclusionName,
    selectedIntegrations,
    selectedAccounts,
    originalExclusionName,
    originalSelectedIntegrations,
    originalSelectedAccounts,
  ]);

  // Check for changes when modal is open or checkForChanges changes
  useEffect(() => {
    if (isExclusionModalOpen) {
      checkForChanges();
    }
  }, [isExclusionModalOpen, checkForChanges]);

  // We've moved this function to the ExclusionWizardModal component

  return (
    <Box
      ml={{ base: '16px', md: '40px' }}
      mr={{ base: '16px', md: '72px' }}
      py={10}
    >
      <Flex
        justify={{ base: 'flex-start', md: 'space-between' }}
        align={{ base: 'flex-start', md: 'center' }}
        mb={8}
        direction={{ base: 'column', md: 'row' }}
      >
        <HeadingText variant="heading2">Manage Exclusions</HeadingText>
        {exclusions.length > 0 && (
          <Button
            onClick={handleAddExclusion}
            leadingIcon={AddIcon}
            mt={{ base: 4, md: 0 }}
          >
            Add Exclusion
          </Button>
        )}
      </Flex>

      {loading && exclusions.length === 0 ? (
        <Flex justify="center" align="center" height="300px">
          <Spinner size="xl" />
        </Flex>
      ) : exclusions.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={{ base: 6, md: 10 }}
          px={4}
          minH="300px"
        >
          <Box mb={4} mt={8}>
            <Image src={PrivilegeImage} alt="Exclusion" />
          </Box>
          <HeadingText size="small" mb={2} textAlign="center">
            Add an exclusion
          </HeadingText>
          <Text
            textAlign="center"
            color="gray.600"
            maxW="450px"
            fontSize={{ base: 'sm', md: 'md' }}
          >
            Add an exclusion to exclude integrations from accounts throughout
            ProfitAssist.
          </Text>
          <Button
            onClick={handleAddExclusion}
            leadingIcon={AddIcon}
            mt={{ base: 4, md: 0 }}
          >
            Add Exclusion
          </Button>
        </Flex>
      ) : (
        <Box overflow="hidden" bg="white">
          <Box overflowX="auto">
            <Table>
              <TableHead>
                <TableRow variant="filled">
                  <TableHeaderSortCell
                    onSort={direction => handleSort('name', direction)}
                    direction={
                      sortConfig.key === 'name'
                        ? sortConfig.direction
                        : undefined
                    }
                  >
                    Exclusion Name
                  </TableHeaderSortCell>
                  <TableHeaderSortCell
                    onSort={direction =>
                      handleSort('assigned_integrations', direction)
                    }
                    direction={
                      sortConfig.key === 'assigned_integrations'
                        ? sortConfig.direction
                        : undefined
                    }
                  >
                    Excluded Integrations
                  </TableHeaderSortCell>
                  <TableHeaderSortCell
                    onSort={direction =>
                      handleSort('assigned_items', direction)
                    }
                    direction={
                      sortConfig.key === 'assigned_items'
                        ? sortConfig.direction
                        : undefined
                    }
                  >
                    Excluded Accounts
                  </TableHeaderSortCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map(exclusion => (
                  <TableRow
                    key={exclusion.id}
                    variant="bottom-divider"
                    bg={
                      (editLoading && editingExclusionId === exclusion.id) ||
                      (isDeleting && deletingExclusionId === exclusion.id)
                        ? 'blue.50'
                        : 'white'
                    }
                  >
                    <TableCell>
                      <Flex align="center">
                        {editLoading && editingExclusionId === exclusion.id ? (
                          <Spinner size="sm" color="blue.500" mr={2} />
                        ) : isDeleting &&
                          deletingExclusionId === exclusion.id ? (
                          <Spinner size="sm" color="red.500" mr={2} />
                        ) : null}
                        <Text>{exclusion.name}</Text>
                      </Flex>
                    </TableCell>
                    <TableCell>
                      <Text>
                        {formatIntegrations(exclusion.assigned_integrations)}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Flex align="center" justify="space-between">
                        <Text>
                          {(() => {
                            // Calculate total accounts across all integrations
                            let totalAccounts = 0;
                            let firstAccountName = '';

                            // Try to use integration-specific account data first
                            if (
                              exclusion.assigned_integrations &&
                              exclusion.assigned_integrations.length > 0
                            ) {
                              // Get all accounts from all integrations
                              const allAccounts = [];

                              exclusion.assigned_integrations.forEach(
                                integId => {
                                  const integrationKey = `assigned_plparent_${integId}`;
                                  if (
                                    exclusion[integrationKey] &&
                                    exclusion[integrationKey].length > 0
                                  ) {
                                    // Add all accounts from this integration
                                    exclusion[integrationKey].forEach(
                                      accountId => {
                                        if (!allAccounts.includes(accountId)) {
                                          allAccounts.push(accountId);

                                          // If this is the first account, try to get its name
                                          if (
                                            firstAccountName === '' &&
                                            accountId
                                          ) {
                                            // Look for account description
                                            for (const integId in accounts) {
                                              const accountList =
                                                accounts[integId];
                                              if (!accountList) continue;

                                              const account = accountList.find(
                                                acc => acc.pk === accountId
                                              );
                                              if (account && account.desc) {
                                                firstAccountName = account.desc;
                                                break;
                                              }
                                            }
                                          }
                                        }
                                      }
                                    );
                                  }
                                }
                              );

                              totalAccounts = allAccounts.length;
                            }

                            // If we couldn't get integration-specific data, fall back to assigned_items
                            if (
                              totalAccounts === 0 &&
                              exclusion.assigned_items
                            ) {
                              totalAccounts = exclusion.assigned_items.length;

                              // Try to get the first account name from account_descriptions
                              if (
                                exclusion.account_descriptions &&
                                exclusion.account_descriptions.length > 0
                              ) {
                                firstAccountName =
                                  exclusion.account_descriptions[0];
                              }
                            }

                            // Format the display text
                            if (totalAccounts === 0) {
                              return '--';
                            } else if (totalAccounts === 1) {
                              return firstAccountName || 'One Account';
                            } else {
                              // Show first account name + count of others
                              return `${firstAccountName} + ${totalAccounts - 1} ${totalAccounts === 2 ? 'Other' : 'Others'}`;
                            }
                          })()}
                        </Text>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<MoreVerticalIcon />}
                            variant="ghost"
                            borderRadius="full"
                            size="sm"
                            isDisabled={
                              isDeleting && deletingExclusionId === exclusion.id
                            }
                          />
                          <MenuList borderRadius="md" bg="gray.100">
                            <MenuItem
                              height="48px"
                              bg="gray.100"
                              icon={<Icon as={EditIcon} w="24px" h="24px" />}
                              onClick={() => handleEditExclusion(exclusion)}
                              _hover={{ bg: '#D3E5FF' }}
                              borderRadius="md"
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              height="48px"
                              bg="gray.100"
                              icon={<Icon as={DeleteIcon} w="24px" h="24px" />}
                              onClick={() => openDeleteDialog(exclusion.id)}
                              _hover={{ bg: '#D3E5FF' }}
                              borderRadius="md"
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Flex>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {/* Exclusion Wizard Modal */}
      <ExclusionWizardModal
        isOpen={isExclusionModalOpen}
        onClose={closeExclusionModal}
        selectedExclusion={selectedExclusion}
        exclusionName={exclusionName}
        setExclusionName={setExclusionName}
        integrations={integrations}
        accounts={accounts}
        selectedIntegrations={selectedIntegrations}
        setSelectedIntegrations={setSelectedIntegrations}
        selectedAccounts={selectedAccounts}
        setSelectedAccounts={setSelectedAccounts}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmitExclusion}
        hasChanges={hasChanges}
      />

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => handleDeleteExclusion(exclusionToDelete)}
        title="Delete Exclusion"
        body="Deleting this exclusion cannot be undone."
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        confirmButtonColor="#D54041"
        isLoading={isDeleting}
      />
    </Box>
  );
};

export default ManageExclusions;
